import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import Flatpickr from 'flatpickr';
import useErrorState from '../../../utils/errors/useErrorState';
import { errorMessages } from '../../../utils/errors/constants';
import { resolve } from '../../../utils/object/pathResolver';
import {
  calculateAgeAtDate,
  formatDateToString,
  geDateOffset,
  parseDateString,
} from '../../../utils/dateUtils/dateUtils';
import Tooltip from '../../common/Tooltip';

const useFlatpickrInstance = (datepickerRef, fields, dispatch, onDateSelect) => {
  const instanceRef = useRef(null);

  useEffect(() => {
    if (!datepickerRef.current || instanceRef.current) {
      return;
    }

    instanceRef.current = Flatpickr(datepickerRef.current, {
      dateFormat: 'd/m/Y',
      minDate: geDateOffset(fields.minDateOffset?.value),
      maxDate: geDateOffset(fields.maxDateOffset?.value),
      disableMobile: true,
      onChange: selectedDates => {
        if (selectedDates.length === 0) {
          return;
        }

        const selectedDateObj = selectedDates[0];

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const normalizedInput = new Date(selectedDateObj);
        normalizedInput.setHours(0, 0, 0, 0);

        const dateStr = formatDateToString(selectedDateObj);
        onDateSelect(dateStr);

        if (normalizedInput < today) {
          return;
        }

        dispatch({
          type: fields.reduxDispatchType?.value,
          payload: dateStr,
        });
      },
    });

    return () => {
      instanceRef.current?.destroy();
      instanceRef.current = null;
    };
  }, [datepickerRef, fields, dispatch, onDateSelect]);

  return instanceRef;
};

const DatePicker = ({
  fields,
  rendering,
  displayErrors,
  onError,
  defaultValue = '',
  coverStartDate,
  dependents,
  prospectDetails,
}) => {
  const dispatch = useDispatch();
  const selectedDate = useSelector(state => state.quoteData.prospectDetails.coverStartDate);

  const datepickerRef = useRef(null);

  const [inputValue, setInputValue] = useState(defaultValue);
  const [error, setError] = useErrorState(rendering.uid, fields.name?.value, fields, onError);

  const hasVisibleError = error && displayErrors;
  const errorClass = hasVisibleError ? ' vds-form__input--invalid' : '';

  const validateAges = selectedCoverDate => {
    if (!selectedCoverDate) {
      return null;
    }

    const primaryDob = prospectDetails?.dateOfBirth;
    const partnerDob = dependents?.spouse?.dateOfBirth;
    const children = dependents?.children || [];

    if (primaryDob) {
      const age = calculateAgeAtDate(primaryDob, selectedCoverDate);
      if (age >= fields.adultMaxAge?.value) {
        return fields.prospectTooOld?.value;
      }
    }
    if (partnerDob) {
      const age = calculateAgeAtDate(partnerDob, selectedCoverDate);
      if (age >= fields.adultMaxAge?.value) {
        return fields.partnerTooOld?.value;
      }
    }
    for (const child of children) {
      const age = calculateAgeAtDate(child.dateOfBirth, selectedCoverDate);
      if (age >= fields.childMaxAge?.value) {
        return fields.childTooOld?.value;
      }
    }

    return null;
  };

  const handleDateSelect = dateStr => {
    setInputValue(dateStr);
  };

  const flatpickrInstance = useFlatpickrInstance(datepickerRef, fields, dispatch, handleDateSelect);

  useEffect(() => {
    const initialDateStr = selectedDate || coverStartDate || defaultValue;
    const dateObj = parseDateString(initialDateStr);

    if (dateObj && flatpickrInstance.current) {
      flatpickrInstance.current.setDate(dateObj, false);
      setInputValue(initialDateStr);
    }
  }, [selectedDate, coverStartDate, defaultValue, flatpickrInstance]);

  useEffect(() => {
    if (fields.required?.value && !inputValue) {
      setError(errorMessages.EMPTY);
    }
    return () => setError(null);
  }, [inputValue, fields.required?.value]);

  useEffect(() => {
    if (!inputValue) {
      return;
    }

    const dateObj = parseDateString(inputValue);

    if (dateObj) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const normalizedInput = new Date(dateObj);
      normalizedInput.setHours(0, 0, 0, 0);

      if (normalizedInput < today) {
        setError({
          message: fields?.pastCoverStartDate?.value,
        });
        return;
      }
    }

    const validationError = validateAges(inputValue);

    if (validationError) {
      setError(validationError);
    } else {
      setError(null);
    }
  }, [inputValue, prospectDetails, dependents]);

  const handleButtonClick = () => flatpickrInstance.current?.open();
  return (
    <div className="vds-form__group">
      <div className="vds-form__group vds-form__group--limited-width">
        <p className="vds-form__label">
          {fields.label?.value}
          {fields.tooltipText?.value && (
            <Tooltip tooltipText={fields.tooltipText} fields={fields} />
          )}
        </p>
        <div className="vds-form__datepicker">
          <input
            id={fields.name?.value}
            name={fields.name?.value}
            ref={datepickerRef}
            className={`vds-form__datepicker-input vds-form__datepicker-input--single ${errorClass}`}
            type="text"
            placeholder={fields.placeholder?.value}
            required={fields.required?.value}
            data-testid={fields.testid?.value}
            value={inputValue}
          />
          <div className="vds-form__overlay"></div>
          <button
            className="vds-form__datepicker-button"
            type="button"
            title="Select a date"
            onClick={handleButtonClick}
            data-testid={`${fields.testid?.value}-button`}
          ></button>
        </div>
      </div>
      {displayErrors && fields.inlineError?.value && error && (
        <div className="vds-form__group vds-form__group--limited-width">
          <div className="vds-form__validation-message vds-form__validation-message--invalid">
            <span className="vds-form__validation-text">{error.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

DatePicker.propTypes = {
  fields: PropTypes.shape({
    minDateOffset: PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    maxDateOffset: PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    reduxDispatchType: PropTypes.shape({
      value: PropTypes.string,
    }),
    reduxValuePath: PropTypes.shape({
      value: PropTypes.string,
    }),
    name: PropTypes.shape({
      value: PropTypes.string,
    }),
    required: PropTypes.shape({
      value: PropTypes.bool,
    }),
    adultMaxAge: PropTypes.shape({
      value: PropTypes.number,
    }),
    childMaxAge: PropTypes.shape({
      value: PropTypes.number,
    }),
    prospectTooOld: PropTypes.shape({
      value: PropTypes.string,
    }),
    partnerTooOld: PropTypes.shape({
      value: PropTypes.string,
    }),
    childTooOld: PropTypes.shape({
      value: PropTypes.string,
    }),
    label: PropTypes.shape({
      value: PropTypes.string,
    }),
    tooltipText: PropTypes.shape({
      value: PropTypes.string,
    }),
    placeholder: PropTypes.shape({
      value: PropTypes.string,
    }),
    testid: PropTypes.shape({
      value: PropTypes.string,
    }),
    inlineError: PropTypes.shape({
      value: PropTypes.bool,
    }),
    pastCoverStartDate: PropTypes.shape({
      value: PropTypes.string,
    }),
  }).isRequired,
  rendering: PropTypes.shape({
    uid: PropTypes.string.isRequired,
  }).isRequired,
  displayErrors: PropTypes.bool,
  onError: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  coverStartDate: PropTypes.string,
  dependents: PropTypes.shape({
    spouse: PropTypes.shape({
      dateOfBirth: PropTypes.string,
    }),
    children: PropTypes.arrayOf(
      PropTypes.shape({
        dateOfBirth: PropTypes.string,
      })
    ),
  }),
  prospectDetails: PropTypes.shape({
    dateOfBirth: PropTypes.string,
  }),
};

DatePicker.defaultProps = {
  defaultValue: '',
  displayErrors: false,
  coverStartDate: '',
  dependents: null,
  prospectDetails: null,
};

const mapStateToProps = (state, props) => {
  const reduxValuePath = props.fields.reduxValuePath?.value;
  const resolvedValue = reduxValuePath ? resolve(reduxValuePath, state) : '';

  return {
    ...props,
    defaultValue: resolvedValue || '',
    displayErrors: props.displayErrors || state.appState.displayErrors,
    coverStartDate: state.quoteData.prospectDetails?.coverStartDate,
    dependents: state.quoteData.prospectDetails?.dependents,
    prospectDetails: state.quoteData.prospectDetails,
  };
};

export default connect(mapStateToProps)(DatePicker);
